import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function Pondicherry() {
    const [openFaq, setOpenFaq] = useState(null);
    const navigate = useNavigate();

    const faqs = [
        {
            q: "How can I book a self drive car in Pondicherry with Wow carz?",
            a: "Booking is simple: visit the website or app, choose your preferred car, select a package (hourly/daily/weekly or km-based), and confirm. You'll receive booking confirmation and pickup details for your Pondicherry ride.",
        },
        {
            q: "What documents are required to rent a car in Pondicherry?",
            a: "You need a valid driving license (minimum age 21 with at least 1 year of driving experience) and a government-issued ID such as Aadhaar or Passport.",
        },
        {
            q: "Can I rent a car for short trips within Pondicherry?",
            a: "Yes — Wow carz offers flexible hourly and daily packages perfect for exploring Rock Beach, White Town, Auroville, cafes, and local shopping spots.",
        },
        {
            q: "Is it possible to take the rental car outside Pondicherry?",
            a: "Yes — you can opt for outstation packages to travel to nearby destinations like Chennai, Mahabalipuram, Chidambaram, or Yelagiri. Check package details for distance limits and charges.",
        },
        {
            q: "Why should I choose Wow carz over other car rental services in Pondicherry?",
            a: "Wow carz offers easy booking, transparent pricing, well-maintained vehicles, and 24/7 customer support—giving you a smooth and flexible travel experience in and around Pondicherry.",
        },
        {
            q: "Are Wow carz rentals available for both short and long durations?",
            a: "Yes — choose from hourly rentals for quick city trips or daily/weekly packages for longer stays, weekend getaways, or outstation travel from Pondicherry.",
        },
    ];

    const places = [
        {
            image: "/assets/paradise-beach.jpg",
            title: "Paradise Beach",
            desc: "One of the most beautiful beaches in Pondicherry, accessible by boat from Chunnambar. Known for golden sand, clear water, and peaceful vibes.",
        },
        {
            image: "/assets/promenade.jpg",
            title: "Promenade Beach",
            desc: "A lively beachfront in the heart of Pondicherry, perfect for evening walks, street views, and enjoying the sea breeze.",
        },
        {
            image: "/assets/serenity.jpg",
            title: "Serenity Beach",
            desc: "A popular beach for surfing and relaxing, known for its calm surroundings and scenic sunrise views.",
        },
        {
            image: "/assets/chunnambar.jpg",
            title: "Chunnambar Boat House",
            desc: "A scenic backwater destination offering boating, kayaking, and access to Paradise Beach.",
        },
        {
            image: "/assets/french-quarter.png",
            title: "French Quarter (White Town)",
            desc: "Famous for colorful colonial buildings, cafes, and charming streets reflecting French architecture and culture.",
        },
        {
            image: "/assets/botanical-garden.jpg",
            title: "Botanical Garden",
            desc: "A peaceful green space in Pondicherry featuring a variety of exotic plants, trees, and a musical fountain. Perfect for a relaxing walk with family.",
        },
        {
            image: "/assets/puducherry-museum.jpg",
            title: "Puducherry Museum",
            desc: "A cultural museum showcasing French colonial artifacts, sculptures, and historical items that reflect the rich heritage of Pondicherry.",
        },
        {
            image: "/assets/varadaraja-temple.jpg",
            title: "Sri Varadaraja Perumal Temple",
            desc: "An ancient Hindu temple dedicated to Lord Vishnu, known for its beautiful Dravidian architecture and spiritual significance in Pondicherry.",
        },
        {
            image: "/assets/sacred-heart-church.jpg",
            title: "Basilica of the Sacred Heart of Jesus",
            desc: "A stunning Gothic-style church in Pondicherry known for its beautiful stained glass panels, peaceful atmosphere, and impressive architecture that attracts visitors and devotees alike.",
        },

    ];

    const nearplaces = [
        {
            image: "/assets/auroville.jpg",
            title: "Auroville (12 km)",
            desc: "A unique experimental township famous for the golden Matrimandir and peaceful environment, attracting visitors from around the world.",
        },
        {
            image: "/assets/chidambaram.jpg",
            title: "Chidambaram Temple (65 km)",
            desc: "A famous temple dedicated to Lord Nataraja, known for its spiritual importance and Dravidian architecture.",
        },
        {
            image: "/assets/gingee.jpg",
            title: "Gingee Fort (70 km)",
            desc: "A historic hill fort known as the 'Troy of the East', offering trekking, panoramic views, and rich history.",
        },
        {
            image: "/assets/pichavaram.jpg",
            title: "Pichavaram Mangrove Forest (75 km)",
            desc: "One of the largest mangrove forests in India where you can enjoy unique boat rides through dense green waterways.",
        },
        {
            image: "/assets/mahabalipuram.jpg",
            title: "Mahabalipuram (100 km)",
            desc: "A UNESCO World Heritage site known for Shore Temple, rock-cut caves, and ancient stone carvings along the coast.",
        },

    ];

    const whyCards = [
        {
            title: "Easy Booking in Pondicherry",
            text: "Book your self-drive car in Pondicherry within minutes and start your journey smoothly across the city or nearby beaches.",
        },
        {
            title: "Safe & Well-Maintained Cars",
            text: "Our vehicles are regularly serviced and sanitized, ensuring a safe and comfortable drive around Pondicherry.",
        },
        {
            title: "Flexible Rental Plans",
            text: "Choose hourly, daily, or outstation packages perfect for beach trips, café hopping, or weekend getaways.",
        },
        {
            title: "Explore Pondicherry & Nearby Attractions",
            text: "Drive to Paradise Beach, Auroville, Promenade Beach, Serenity Beach, or Chunnambar Boat House with complete freedom and comfort."
        },
        {
            title: "Airport & Railway Station Pickup",
            text: "Convenient car delivery at Pondicherry Airport, Pondicherry Railway Station, or your doorstep – anytime you need."
        }
    ];

    // Slider settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        autoplay: true,
        autoplaySpeed: 2500,
        slidesToShow: 3,
        slidesToScroll: 1,
        responsive: [
            {
                breakpoint: 1024,
                settings: { slidesToShow: 2 },
            },
            {
                breakpoint: 768,
                settings: { slidesToShow: 1, arrows: false },
            },
        ],
    };

    return (
        <div className="min-h-screen bg-gray-50 text-gray-800 font-inter">
            {/* Header */}
            <header className="bg-blue-100 shadow-sm sticky top-0 z-20">
                <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
                    <div className="flex-shrink-0">
                        <img
                            onClick={() => navigate("/")}
                            loading="lazy"
                            style={{ height: 55, width: 160 }}
                            src="https://bagzpack.s3.amazonaws.com/logo/68ea80da-f61b-4333-a4ed-ebae07c63893/IMG/1645694302682.png"
                            alt="wowcarzlogo"
                            className="object-cover cursor-pointer"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <a
                            href="/"
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700 transition"
                        >
                            Book Now
                        </a>
                        <a
                            href="tel:+919844300066"
                            className="inline-flex items-center gap-2 border border-gray-200 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 transition"
                        >
                            📞 Call Us
                        </a>
                    </div>
                </div>
            </header>

            <main className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* Hero */}
                <section className="relative rounded-2xl h-[500px] sm:h-[550px] lg:h-[500px] overflow-hidden isolate">

                    <img
                        src="/assets/pond3.jpg"
                        alt="Self Drive Cars Pondicherry"
                        className="absolute inset-0 w-full h-full object-cover rounded-2xl"
                    />

                    <div className="absolute inset-0 bg-black/50 flex flex-col justify-center items-center text-center px-6 sm:px-12">
                        <h2 className="text-5xl sm:text-5xl font-bold text-white mb-4">
                            Self Drive Cars in Pondicherry
                        </h2>

                        <p className="text-gray-200 text-lg sm:text-xl font-bold lg:text-3xl mb-6 max-w-3xl leading-relaxed">
                            Explore Pondicherry and nearby destinations with hassle-free self drive
                            cars from Wow Carz.
                        </p>
                    </div>

                </section>


                {/* Explore Section */}
                <section className="bg-blue-200 p-5 m-5">
                    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-3xl p-8 sm:p-12 text-center">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-6">
                            Explore Pondicherry with Wow Carz
                        </h2>
                        <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                            Explore Pondicherry with the comfort and freedom of a self drive car rental designed for modern travelers. Wow Carz offers reliable and premium self drive cars in Pondicherry, making it easy to travel across the city or plan a relaxing getaway whenever you want.

                            Perfect for business travel, family outings, weekend road trips, and short trips within 100 km, Wow Carz provides flexible self drive car rental in Pondicherry without the hassle of fixed schedules or driver dependency. Simply pick your car, start your journey, and enjoy complete travel freedom.

                            Choose Wow Carz for affordable, safe, and convenient self drive car rentals in Pondicherry and experience the joy of exploring the city and nearby destinations at your own pace.
                        </p>
                    </div>
                </section>

                {/* Why Rent Section (Slider) */}
                <section className="py-12 bg-gray-50">
                    <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10 text-gray-900">
                        Why Rent With Wow Carz?
                    </h2>
                    <Slider {...sliderSettings} className="px-6 [&_.slick-dots]:left-0">
                        {whyCards.map((card, i) => (
                            <div key={i} className="px-3">
                                <div className="bg-white rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition">
                                    <h3 className="text-xl font-semibold mb-2">{card.title}</h3>
                                    <p className="text-gray-600 text-sm">{card.text}</p>
                                </div>
                            </div>
                        ))}
                    </Slider>
                </section>

                {/* Plan Your Trip */}
                <section className="py-12 sm:py-16 bg-blue-300 mx-4 sm:mx-8 lg:mx-16 rounded-2xl">
                    <div className="max-w-7xl mx-auto text-center px-4">

                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-10 text-gray-900">
                            Plan Your Trip to Pondicherry
                        </h2>

                        <Slider {...sliderSettings} className="px-2">

                            {/* Card 1 */}
                            <div className="px-3">
                                <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
                                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 text-2xl">
                                        🏛️
                                    </div>

                                    <h3 className="text-lg sm:text-xl font-semibold mb-3">
                                        Interesting Facts About Pondicherry
                                    </h3>

                                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                        Pondicherry is a former French colony known for its unique blend of French and Indian culture, charming streets, colorful buildings, and peaceful coastal vibe.
                                    </p>
                                </div>
                            </div>

                            {/* Card 2 */}
                            <div className="px-3">
                                <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
                                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 text-2xl">
                                        🎓
                                    </div>

                                    <h3 className="text-lg sm:text-xl font-semibold mb-3">
                                        Education & Culture Hub
                                    </h3>

                                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                        Pondicherry is home to reputed institutions like Pondicherry University and is known for its calm environment, cultural diversity, and artistic communities like Auroville.
                                    </p>
                                </div>
                            </div>

                            {/* Card 3 */}
                            <div className="px-3">
                                <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
                                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-100 text-red-600 text-2xl">
                                        🎉
                                    </div>

                                    <h3 className="text-lg sm:text-xl font-semibold mb-3">
                                        Famous Festivals in Pondicherry
                                    </h3>

                                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                        Popular festivals include Bastille Day, Pongal, Diwali, and the International Yoga Festival, reflecting the city’s unique mix of French and Indian traditions.
                                    </p>
                                </div>
                            </div>

                            {/* Card 4 */}
                            <div className="px-3">
                                <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
                                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-yellow-100 text-yellow-600 text-2xl">
                                        🌤️
                                    </div>

                                    <h3 className="text-lg sm:text-xl font-semibold mb-3">
                                        Best Time to Visit Pondicherry
                                    </h3>

                                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                        The best time to visit Pondicherry is from October to March when the weather is pleasant for beach visits, sightseeing, and exploring nearby attractions.
                                    </p>
                                </div>
                            </div>

                        </Slider>
                    </div>
                </section>




                {/* Near Places Section (Slider) */}
                <section className="mt-12">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
                        Places Near by Pondicherry
                    </h3>
                    <p className="text-gray-600 mb-6 text-center">
                        Perfect for day trips and weekend getaways with your Wow Carz ride.
                    </p>
                    <Slider {...sliderSettings} className="px-6 [&_.slick-dots]:left-0">
                        {nearplaces.map((place) => (
                            <div key={nearplaces.title} className="px-3">
                                <article className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl border border-gray-100 text-center transition">
                                    <img
                                        className="w-48 h-48 object-cover rounded-xl mx-auto mb-4"
                                        src={place.image}
                                        alt={place.title}
                                    />
                                    <h4 className="font-semibold text-lg mb-2">{place.title}</h4>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        {place.desc}
                                    </p>
                                </article>
                            </div>
                        ))}
                    </Slider>
                </section>




                {/* Top Places */}
                <section className="mt-16">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
                        Top Places to Visit in and Around Pondicherry
                    </h3>
                    <p className="text-gray-600 mb-6 text-center">
                        Explore iconic landmarks, serene gardens, and historic towns with
                        your Wow Carz ride.
                    </p>
                    {/* Top place(PC view)*/}
                    <div className="hidden lg:grid grid-cols-3 gap-8 px-4 max-w-6xl mx-auto">
                        {places.map((place) => (
                            <div
                                key={place.title}
                                className="bg-white rounded-xl shadow-md hover:shadow-xl transition p-6 text-center"
                            >
                                <img
                                    src={place.image}
                                    alt={place.title}
                                    className="w-full h-56 object-cover rounded-lg mb-4"
                                />

                                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                                    {place.title}
                                </h4>

                                <p className="text-gray-600 text-sm">
                                    {place.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                    {/* Top place(Mobile view)*/}
                    <div className="block lg:hidden max-w-xl mx-auto">
                        <Slider {...sliderSettings}>
                            {places.map((place) => (
                                <div key={place.title} className="px-3">
                                    <div className="bg-white rounded-xl shadow-md p-6 text-center">

                                        <img
                                            src={place.image}
                                            alt={place.title}
                                            className="w-full h-52 object-cover rounded-lg mb-4"
                                        />

                                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                            {place.title}
                                        </h4>

                                        <p className="text-gray-600 text-sm">
                                            {place.desc}
                                        </p>

                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>

                </section>




                {/* FAQ */}
                <section className="mt-16">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
                        Frequently Asked Questions
                    </h3>
                    <div className="space-y-4">
                        {faqs.map((f, i) => (
                            <div
                                key={i}
                                className="border rounded-xl bg-white shadow-sm overflow-hidden"
                            >
                                <button
                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition"
                                >
                                    <span className="font-medium text-gray-800">{f.q}</span>
                                    <span className="text-gray-500 text-lg">
                                        {openFaq === i ? "−" : "+"}
                                    </span>
                                </button>
                                <div
                                    className={`px-5 overflow-hidden transition-all duration-300 ${openFaq === i ? "max-h-40 py-3" : "max-h-0"
                                        }`}
                                >
                                    <p className="text-gray-700 text-sm leading-relaxed">{f.a}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* CTA */}
                <section className="mt-16 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 text-white p-8 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="text-center sm:text-left">
                        <h4 className="text-2xl font-semibold">
                            Ready to Start Your Journey?
                        </h4>
                        <p className="text-sm mt-2 text-blue-100">
                            Experience hassle-free driving with Wow Carz today.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <a
                            href="/"
                            className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-100 transition"
                        >
                            Book Now
                        </a>
                        <a
                            href="tel:+919844300066"
                            className="border border-white px-6 py-3 rounded-lg hover:bg-white hover:text-blue-600 transition"
                        >
                            Call Us
                        </a>
                    </div>
                </section>

                {/* Footer */}
                <footer className="mt-10 text-center text-sm text-gray-500">
                    © {new Date().getFullYear()} Wow Carz — All rights reserved
                </footer>
            </main>
        </div>
    );
}

export default Pondicherry;
